import simplepolygon from 'simplepolygon';
import { type DeepWritable } from 'ts-essentials';
import {
  jtsParse, operations,
} from '~/_shared/types/polygon/jsts.utils';
import {
  type GeoJsonFeature, type GeoJsonFeatureCollection, type Geometry, type MultiPolygon, type MultiPolygonCoordinates,
  type Polygon, type PolygonCoordinates, type PolygonGeometry, type PolygonPath,
} from '~/_shared/types/polygon/polygon.types';
import { notEmpty } from '~/_shared/utils/array/array.helpers';
import { getPolygonPathFromLatLngBounds } from '~/_shared/utils/map/latLngBounds.factory';
import { cloneDeep } from '~/_shared/utils/object/deepMerge';
import { type BoundingBox } from '~/map/map/boundingBox';
import { type LatLng } from '../latLng';

/////////////////////////////
//#region Geometry to Polygon

export const convertGeometryToMultiPolygon = (geometry: Geometry | GeoJsonFeatureCollection<any> | GeoJsonFeature<any>): MultiPolygon => {
  let paths: ReadonlyArray<MultiPolygonCoordinates> = [];

  switch (geometry.type) {
    case 'Polygon': {
      paths = [geometry.coordinates];
      break;
    }

    case 'MultiPolygon': {
      paths = geometry.coordinates;
      break;
    }

    case 'GeometryCollection': {
      return geometry.geometries.flatMap(convertGeometryToMultiPolygon);
    }

    case 'Feature': {
      return convertGeometryToMultiPolygon(geometry.geometry);
    }

    case 'FeatureCollection': {
      return geometry.features.flatMap(feature => convertGeometryToMultiPolygon(feature.geometry));
    }

    default: {
      break;
    }
  }

  return convertCoordinateToMultiPolygon(paths);
};

export const convertCoordinateToMultiPolygon = (multiPolygonCoordinates: ReadonlyArray<MultiPolygonCoordinates>): MultiPolygon =>
  multiPolygonCoordinates.filter(c => c.length).map(convertCoordinateToPolygon);

export const convertCoordinateToPolygon = (polygonCoordinates: NonEmptyArray<PolygonCoordinates>): Polygon => {
  const convert = (coordinates: PolygonCoordinates) => removeLoop(coordinates.map(c => convertCoordinateToLatLng(c)));

  return {
    path: convert(polygonCoordinates[0]),
    holes: polygonCoordinates.slice(1).map(c => convert(c)),
  };
};

export const convertLatLngToCoordinate = (point: LatLng): [number, number] => [point.lng, point.lat];

export const convertCoordinateToLatLng = (coordinates: [number, number]): LatLng => {
  return ({
    lat: coordinates[1],
    lng: coordinates[0],
  });
};

const removeLoop = (path: LatLng[]): LatLng[] => {
  return path.slice(0, -1);
};

//#endregion

/////////////////////////////
//#region Polygon to Geometry

export const convertMultiPolygonToGeometry = (multiPolygon: MultiPolygon): Geometry => {
  if (multiPolygon.length === 1 && notEmpty(multiPolygon)) {
    const polygon = multiPolygon[0];

    return ({
      type: 'Polygon',
      coordinates: convertPolygonToCoordinates(polygon),
    });
  }

  return ({
    type: 'MultiPolygon',
    coordinates: multiPolygon.map(convertPolygonToCoordinates),
  });
};

export const convertPolygonToGeometry = (polygon: Polygon): PolygonGeometry => ({
  type: 'Polygon',
  coordinates: convertPolygonToCoordinates(polygon),
});

export const convertBoundingBoxToGeometry = (bb: BoundingBox): PolygonGeometry[] => {
  const polygonPaths = getPolygonPathFromLatLngBounds(bb);
  return polygonPaths.map(polygonPath => convertLatLngPathToGeometry(polygonPath));
};

export const convertLatLngPathToGeometry = (path: PolygonPath): PolygonGeometry => ({
  type: 'Polygon',
  coordinates: [convertLatLngPathToGeometryCoordinates(path)],
});

export const convertPolygonToCoordinates = (polygon: Polygon): ReadonlyArray<PolygonCoordinates> => ([
  convertLatLngPathToGeometryCoordinates(polygon.path),
  ...polygon.holes.map(convertLatLngPathToGeometryCoordinates),
]);

const convertLatLngPathToGeometryCoordinates = (path: PolygonPath): PolygonCoordinates => {
  const firstPathPoint = path[0];
  const loopedPath = [...path, firstPathPoint];

  const coordinates = loopedPath.map(convertLatLngToCoordinate);
  return coordinates;
};

//#endregion

/////////////////////////////
//#region Polygon to WKT

export const convertMultipolygonToWktFormat = (multiPolygon: MultiPolygon) => {
  if (multiPolygon.length === 0) {
    return 'MULTIPOLYGON EMPTY';
  }

  const seenExteriorRings = new Set<string>();

  const polygonArray = multiPolygon
    .reduce((acc, polygon) => {
      const wktPath = convertPathToWktCoordinates(ensurePathCounterClockwise(polygon.path));

      if (seenExteriorRings.has(wktPath)) {
        return acc;
      }

      seenExteriorRings.add(wktPath);
      return [
        ...acc,
        [
          wktPath,
          ...polygon.holes.map(hole => convertPathToWktCoordinates(ensurePathClockwise(hole))),
        ],
      ];
    }, []);

  if (polygonArray.length === 1 && polygonArray[0]) {
    return `POLYGON(${polygonArray[0].join(',')})`;
  }

  return `MULTIPOLYGON(${polygonArray.map(rings => `(${rings.join(',')})`).join(',')})`;
};

const convertPathToWktCoordinates = (path: ReadonlyArray<LatLng>) => {
  if (path.length < 3) {
    throw new Error('A polygon requires at least 3 points.');
  }

  const normalizedPath = [...path];

  const first = normalizedPath[0];
  const last = normalizedPath[normalizedPath.length - 1];

  if (!first || !last) {
    throw new Error('Invalid path');
  }

  if (first.lat !== last.lat || first.lng !== last.lng) {
    normalizedPath.push(first);
  }

  // WKT uses: longitude latitude
  const coordinates = normalizedPath
    .map((p) => `${p.lng} ${p.lat}`)
    .join(',');

  return `(${coordinates})`;
};

const ensurePathClockwise = (path: ReadonlyArray<LatLng>): ReadonlyArray<LatLng> => {
  return getSignedRingArea(path) < 0 ? path : [...path].reverse();
};

const ensurePathCounterClockwise = (path: ReadonlyArray<LatLng>): ReadonlyArray<LatLng> => {
  return getSignedRingArea(path) > 0 ? path : [...path].reverse();
};

// Shoelace formula on (lng=x, lat=y). Returns 2x the planar signed area;
// only the sign is used to detect winding order so we can satisfy WKT's
// requirement that exterior rings are CCW (area > 0) and holes are CW (area < 0).
const getSignedRingArea = (path: ReadonlyArray<LatLng>) => {
  return path.reduce((area, point, index) => {
    const nextPoint = path[(index + 1) % path.length];

    if (!nextPoint) {
      return area;
    }

    return area + (point.lng * nextPoint.lat) - (nextPoint.lng * point.lat);
  }, 0);
};

//#endregion

export const removeGeometryPolygonIntersections = (geometry: PolygonGeometry): GeoJsonFeatureCollection => {
  const feature: GeoJsonFeature = {
    type: 'Feature',
    geometry,
  };

  return simplepolygon(feature);
};

export const flipPolygonsAndHoles = (polygons: readonly Polygon[], globalBounds: PolygonPath): readonly Polygon[] => {
  const polygonHolePaths = polygons
    .flatMap(polygon => polygon.holes);
  const polygonsToBe: readonly Polygon[] = [
    ...polygonHolePaths.map(path => ({ path, holes: [] })),
    { path: globalBounds, holes: [] },
  ];
  const polygonInfos = polygonsToBe.map(polygon => {
    return {
      geometry: jtsParse(convertPolygonToGeometry(polygon)),
      polygon: cloneDeep<DeepWritable<Polygon>>(polygon),
    };
  });
  const holeInfos = polygons.map(holePolygon => {
    return {
      geometry: jtsParse(convertPolygonToGeometry({ path: holePolygon.path, holes: [] })),
      polygon: { path: holePolygon.path, holes: [] },
    };
  });

  // The polygons are sorted so that if A is within B, A comes first in the list so that we can only punch a hole in
  // the first polygon in the list and then break;
  polygonInfos.sort((polygonA, polygonB) => {
    return operations.relate.contains(polygonA.geometry, polygonB.geometry) ? 1 : -1;
  });

  holeInfos.forEach(holeInfo => {
    for (const polygonInfo of polygonInfos) {
      if (operations.relate.contains(polygonInfo.geometry, holeInfo.geometry)) {
        polygonInfo.polygon.holes.push([...holeInfo.polygon.path]);
        break;
      }
    }
  });

  return polygonInfos.map(info => info.polygon);
};

export const getLatLngsOfMultipolygonPoints = (multiPolygon: MultiPolygon): Readonly<LatLng>[] => (
  multiPolygon.reduce((all: Readonly<LatLng>[], polygon) => all.concat(polygon.path), [])
);

export const countMultipolygonVertices = (multiPolygon: MultiPolygon): number =>
  multiPolygon.reduce((sum, p) => sum + p.path.length + p.holes.reduce((s, h) => s + h.length, 0), 0);

export const getGooglePolygonPathsForPolygon = (polygon: Polygon): LatLng[][] => {
  return [polygon.path, ...polygon.holes].map(path => path.map(({ lat, lng }) => ({ lat, lng })));
};
